import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const array = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) array[i] = raw.charCodeAt(i);
  return array;
}

const usePushNotifications = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setSupported(false);
        setIsLoading(false);
        return;
      }
      setSupported(true);
      setPermission(Notification.permission);
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub && Notification.permission === 'granted');
      } catch {
        // ignore
      }
      setIsLoading(false);
    };
    check();
  }, []);

  const subscribe = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Get VAPID public key from server
      const { data } = await axios.get(`${API}/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vapidKey = data.public_key;
      if (!vapidKey) throw new Error('No VAPID public key');

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Permission denied');

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const subJson = sub.toJSON();
      await axios.post(
        `${API}/push/subscribe`,
        { endpoint: subJson.endpoint, keys: subJson.keys, expirationTime: subJson.expirationTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();

      const token = localStorage.getItem('token');
      await axios.delete(`${API}/push/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error('Push unsubscribe error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isSubscribed, isLoading, permission, supported, subscribe, unsubscribe };
};

export default usePushNotifications;
